// src/email/components/SearchBar.jsx
import React, { useEffect, useRef, useState } from 'react';

const FILTER_CHIPS = [
  { label: 'Has attachment', op: 'has:attachment' },
  { label: 'Unread', op: 'is:unread' },
  { label: 'Starred', op: 'is:starred' },
  { label: 'Last 7 days', op: 'newer_than:7d' },
  { label: 'Sent by me', op: 'in:sent' },
];

const DATE_WITHIN = [
  { value: '', label: 'Any time' },
  { value: '1d', label: '1 day' },
  { value: '7d', label: '1 week' },
  { value: '1m', label: '1 month' },
  { value: '1y', label: '1 year' },
];

const SEARCH_IN = [
  { value: '', label: 'All mail' },
  { value: 'inbox', label: 'Inbox' },
  { value: 'sent', label: 'Sent' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'spam', label: 'Spam' },
  { value: 'trash', label: 'Trash' },
];

const EMPTY_ADVANCED = {
  from: '',
  to: '',
  subject: '',
  words: '',
  without: '',
  within: '',
  folder: '',
  hasAttachment: false,
};

function operatorRegex(op, flags) {
  return new RegExp(`(^|\\s)${op.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, flags);
}

function hasOperator(query, op) {
  return operatorRegex(op, 'i').test(query);
}

function toggleOperator(query, op) {
  if (hasOperator(query, op)) {
    return query.replace(operatorRegex(op, 'gi'), '$1').replace(/\s+/g, ' ').trim();
  }
  return `${query.trim()} ${op}`.trim();
}

function quoteValue(value) {
  const v = value.trim().replace(/"/g, '');
  return /\s/.test(v) ? `"${v}"` : v;
}

function buildAdvancedQuery(f) {
  const parts = [];
  if (f.from.trim()) parts.push(`from:${quoteValue(f.from)}`);
  if (f.to.trim()) parts.push(`to:${quoteValue(f.to)}`);
  if (f.subject.trim()) parts.push(`subject:${quoteValue(f.subject)}`);
  if (f.words.trim()) parts.push(f.words.trim());
  f.without
    .split(/\s+/)
    .filter(Boolean)
    .forEach((w) => parts.push(`-${w.replace(/^-+/, '')}`));
  if (f.within) parts.push(`newer_than:${f.within}`);
  if (f.folder) parts.push(`in:${f.folder}`);
  if (f.hasAttachment) parts.push('has:attachment');
  return parts.join(' ');
}

/**
 * Gmail-style search box. The query text is the single source of truth:
 * filter chips and the advanced form only edit that text.
 */
export function SearchBar({ value, onChange, onSubmit, onClear }) {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState(EMPTY_ADVANCED);

  // "/" focuses the search box unless the user is typing somewhere else
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Close the advanced panel on outside click
  useEffect(() => {
    if (!showAdvanced) return undefined;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowAdvanced(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdvanced]);

  const setField = (key) => (e) =>
    setAdvanced((prev) => ({
      ...prev,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const handleAdvancedSubmit = (e) => {
    e.preventDefault();
    const query = buildAdvancedQuery(advanced);
    if (!query) return;
    setShowAdvanced(false);
    onSubmit(query);
  };

  return (
    <div className="mail-search" ref={wrapRef}>
      <div className="mail-search-box">
        <svg className="mail-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="mail-search-input"
          placeholder="Search mail"
          aria-label="Search mail"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSubmit(value);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setShowAdvanced(false);
              onClear();
            }
          }}
        />
        {value && (
          <button
            type="button"
            className="mail-search-icon-btn"
            onClick={() => {
              onClear();
              inputRef.current?.focus();
            }}
            title="Clear search"
            aria-label="Clear search"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className={`mail-search-icon-btn ${showAdvanced ? 'active' : ''}`}
          onClick={() => setShowAdvanced((prev) => !prev)}
          title="Show search options"
          aria-label="Show search options"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="7" y1="12" x2="17" y2="12" />
            <line x1="10" y1="18" x2="14" y2="18" />
          </svg>
        </button>
      </div>

      <div className="mail-search-chips">
        {FILTER_CHIPS.map((chip) => {
          const isOn = hasOperator(value, chip.op);
          return (
            <button
              key={chip.op}
              type="button"
              className={`mail-search-chip ${isOn ? 'is-on' : ''}`}
              aria-pressed={isOn}
              onClick={() => onSubmit(toggleOperator(value, chip.op))}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {showAdvanced && (
        <form className="mail-search-advanced" onSubmit={handleAdvancedSubmit}>
          <label className="mail-search-adv-row">
            <span>From</span>
            <input type="text" value={advanced.from} onChange={setField('from')} autoFocus />
          </label>
          <label className="mail-search-adv-row">
            <span>To</span>
            <input type="text" value={advanced.to} onChange={setField('to')} />
          </label>
          <label className="mail-search-adv-row">
            <span>Subject</span>
            <input type="text" value={advanced.subject} onChange={setField('subject')} />
          </label>
          <label className="mail-search-adv-row">
            <span>Has the words</span>
            <input type="text" value={advanced.words} onChange={setField('words')} />
          </label>
          <label className="mail-search-adv-row">
            <span>Doesn't have</span>
            <input type="text" value={advanced.without} onChange={setField('without')} />
          </label>
          <label className="mail-search-adv-row">
            <span>Date within</span>
            <select value={advanced.within} onChange={setField('within')}>
              {DATE_WITHIN.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="mail-search-adv-row">
            <span>Search in</span>
            <select value={advanced.folder} onChange={setField('folder')}>
              {SEARCH_IN.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="mail-search-adv-check">
            <input
              type="checkbox"
              className="modern-checkbox"
              checked={advanced.hasAttachment}
              onChange={setField('hasAttachment')}
            />
            <span>Has attachment</span>
          </label>
          <div className="mail-search-adv-actions">
            <button type="button" className="btn btn-subtle" onClick={() => setAdvanced(EMPTY_ADVANCED)}>
              Clear
            </button>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
